import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { affFetch } from '../../lib/affApi';

export const ProposalGenerator = ({ isEs }) => {
  const [form, setForm] = useState({ restaurant_name: '', website: '', avg_ticket: '', covers_per_month: '', delivery_share_pct: '' });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const onPrefill = (e) => {
      setForm((p) => ({ ...p, restaurant_name: e.detail.restaurant_name || '', website: e.detail.website || '' }));
      setStatus('');
    };
    window.addEventListener('kx-prefill-proposal', onPrefill);
    return () => window.removeEventListener('kx-prefill-proposal', onPrefill);
  }, []);
  const c = isEs
    ? { title: 'Generador de Propuestas PDF', sub: 'Escribe el restaurante y su web — lo escaneamos EN VIVO y te damos un PDF profesional con su score real, hallazgos y oportunidad de ingresos. Envíalo por WhatsApp en 2 minutos.',
        name: 'Nombre del restaurante', web: 'Web (ej. mirestaurante.com)', ticket: 'Ticket medio $ (opcional)', covers: 'Pedidos/mes (opcional)', delivery: '% delivery apps (opcional)',
        roiHint: 'Si rellenas ticket y pedidos, el PDF incluye la estimación de ROI — la página que cierra ventas.',
        cta: 'Generar propuesta (escaneo en vivo ~20s)', generating: 'Escaneando y generando PDF…',
        ok: 'Propuesta descargada ✅ — envíala por WhatsApp', err: 'No se pudo generar. Revisa la web e inténtalo de nuevo.' }
    : { title: 'Proposal PDF Generator', sub: "Type the restaurant and its website — we scan it LIVE and give you a professional PDF with their real score, findings and revenue opportunity. Send it on WhatsApp in 2 minutes.",
        name: 'Restaurant name', web: 'Website (leave empty if none)', ticket: 'Avg ticket $ (optional)', covers: 'Orders/month (optional)', delivery: '% delivery apps (optional)',
        roiHint: 'Fill in ticket and orders and the PDF includes the ROI estimate — the page that closes deals. No website? The proposal shows their total AI invisibility (your best argument).',
        cta: 'Generate proposal (live scan ~20s)', generating: 'Scanning & building PDF…',
        ok: 'Proposal downloaded ✅ — send it on WhatsApp', err: 'Could not generate. Check the website and try again.' };

  const gen = async (e) => {
    e.preventDefault();
    if (!form.restaurant_name.trim()) return;
    setBusy(true);
    try {
      const body = { restaurant_name: form.restaurant_name, website: form.website.trim(), lang: isEs ? 'es' : 'en', delivery_share_pct: parseFloat(form.delivery_share_pct || '0') };
      if (form.avg_ticket && form.covers_per_month) {
        body.avg_ticket = parseFloat(form.avg_ticket);
        body.covers_per_month = parseInt(form.covers_per_month, 10);
      }
      const r = await affFetch('/me/proposal', { method: 'POST', body: JSON.stringify(body) });
      if (!r.ok) throw new Error('failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `kitxens-proposal-${form.restaurant_name.trim().toLowerCase().replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      setStatus('ok');
      toast.success(c.ok);
    } catch {
      setStatus('error');
      toast.error(c.err);
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full h-11 rounded-xl bg-white border border-neutral-300 px-3.5 text-[13.5px] text-neutral-900 placeholder-neutral-400 outline-none focus:border-[#F77F11] transition-colors';

  return (
    <div className="mt-6 rounded-2xl border-2 border-[#F77F11]/30 bg-[#F77F11]/[0.04] p-6" data-testid="aff-proposal-gen">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#F77F11] font-bold">
        <FileText className="w-4 h-4" /> {c.title}
      </p>
      <p className="mt-1.5 text-[13px] text-neutral-600 leading-relaxed max-w-2xl">{c.sub}</p>
      <form onSubmit={gen} className="mt-4 grid sm:grid-cols-2 gap-3">
        <input required value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })}
          placeholder={c.name} className={inputCls} data-testid="proposal-name-input" />
        <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder={c.web} className={inputCls} data-testid="proposal-website-input" />
        <input type="number" min="1" value={form.avg_ticket} onChange={(e) => setForm({ ...form, avg_ticket: e.target.value })}
          placeholder={c.ticket} className={inputCls} data-testid="proposal-ticket-input" />
        <input type="number" min="1" value={form.covers_per_month} onChange={(e) => setForm({ ...form, covers_per_month: e.target.value })}
          placeholder={c.covers} className={inputCls} data-testid="proposal-covers-input" />
        <input type="number" min="0" max="100" value={form.delivery_share_pct} onChange={(e) => setForm({ ...form, delivery_share_pct: e.target.value })}
          placeholder={c.delivery} className={inputCls} data-testid="proposal-delivery-input" />
        <button type="submit" disabled={busy}
          className="h-11 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[13px] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          data-testid="proposal-generate-btn">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {c.generating}</> : <><Download className="w-4 h-4" /> {c.cta}</>}
        </button>
      </form>
      <p className="mt-2 text-[11.5px] text-neutral-500">{c.roiHint}</p>
      {status === 'ok' && <p className="mt-2 text-[12.5px] text-emerald-600 font-semibold" data-testid="aff-proposal-status">✓ {c.ok}</p>}
      {status === 'error' && <p className="mt-2 text-[12.5px] text-red-500 font-semibold" data-testid="aff-proposal-status">{c.err}</p>}
    </div>
  );
};
